
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAccount } from "wagmi";
import { Button } from "@/components/ui/button";
import CommentForm from "./CommentForm";
import CommentItem from "./CommentItem";
import { useComments } from "@/hooks/useComments";
import { toast } from "@/hooks/use-toast";
import { LoaderCircle } from "lucide-react";

interface CommentsSectionProps {
  coinId: string;
}

const CommentsSection = ({ coinId }: CommentsSectionProps) => {
  const [showCommentForm, setShowCommentForm] = useState(false);
  const { isConnected } = useAccount();
  const { comments, isLoading, addComment, addReply, toggleLike } = useComments(coinId);

  const handleSubmitComment = async (content: string) => {
    await addComment(content);
    setShowCommentForm(false);
  };

  const handleCancelComment = () => {
    setShowCommentForm(false);
  };

  const handleAddComment = () => {
    if (!isConnected) {
      toast({
        title: "Connect wallet",
        description: "Please connect your wallet to post comments",
        variant: "destructive",
      });
      return;
    }
    setShowCommentForm(true);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Comments</CardTitle>
        {!showCommentForm && (
          <Button size="sm" onClick={handleAddComment}>
            Add Comment
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {showCommentForm && (
          <CommentForm
            onSubmit={handleSubmitComment}
            onCancel={handleCancelComment}
            isLoading={isLoading}
          />
        )}
        
        <div className="space-y-6 mt-4">
          {isLoading && comments.length === 0 ? (
            <div className="flex justify-center py-8">
              <LoaderCircle className="w-8 h-8 animate-spin text-primary/70" />
            </div>
          ) : comments.length === 0 ? (
            <p className="text-center text-muted-foreground py-6">
              No comments yet. Be the first to share your thoughts!
            </p>
          ) : (
            comments.map((comment) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                onReply={(content) => addReply(content, comment.id)}
                onLike={() => toggleLike(comment.id)}
                isLoading={isLoading}
              />
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CommentsSection;
